import { activeSeconds } from './game.js';
// FLOW Platform — Post-game screen.
//
// Rendered after /plays accepts the submission. Loads /streak and /stats
// (in parallel) and shows: outcome banner, the completed chain, stars
// and time, current/best streak, a personal-stats summary and a Share
// button. Feedback is the platform's shared button (flow-feedback-v1.js),
// placed by the shortcode outside this mount — the game's own "Puzzle
// feedback" panel was retired 2026-09-26 (Jeff: "thruword has two feedback
// buttons, conform to the other games").
//
// All UI is plain DOM. The module owns its own DOM tree once mounted
// and replaces the entire game container.

import { createLiveRegion, focusElement } from './a11y.js';
import { compoundDisplay } from './compound-graph.js';
import { FlowApiError } from './api-client.js';
import { betaNextUnplayedId, betaRandomUnplayedId } from './shell.js';
import { recordBetaPlay } from './stats-view.js';

const REPORT_REASONS = [
  { value: 'too_easy', label: 'Too easy' },
  { value: 'too_hard', label: 'Too hard' },
  { value: 'has_error', label: 'Has an error' },
  { value: 'tone', label: 'Tone concern' },
  { value: 'other', label: 'Other' },
];

/**
 * Mount the post-game screen.
 *
 * Arguments:
 *   container          - DOM element to render into (replaces its contents).
 *   gameState          - The final game state from game.js.
 *   playResponse       - The unwrapped response from POST /plays.
 *   api                - The shared API client.
 */
export function mountPostGame(container, gameState, playResponse, api, opts) {
  opts = opts || {};
  const testMode = !!opts.testMode;

  container.innerHTML = '';
  container.classList.add('flow-fht-postgame');

  // 2.14.29 (Jeff, 2026-09-30: "Has two shares at bottom"): this screen has its own Share,
  // so the platform's bottom Share steps out while it is up. Feedback stays. The game
  // screen puts it back (shell.js).
  showBottomShare(false);

  const live = createLiveRegion(container);

  const solved = gameState.status === 'solved';

  // Outcome banner. Declared up here so the focus/announce calls at the end
  // can reference it. The banner + chain are built inside a guard below so a
  // hiccup there can never stop the Share / feedback buttons from rendering.
  let banner = null;
  try {
    banner = document.createElement('div');
    banner.className = 'flow-fht-banner flow-fht-banner-' + (solved ? 'solved' : 'failed');
  banner.setAttribute('role', 'status');
  banner.setAttribute('tabindex', '-1');

  // Daily identity: "thruWord #N" — the number people brag about. No level
  // since 2.14.13: there is one puzzle a day, as in the other games.
  const dailyN = Number(gameState.daily_number) || 0;
  if (!testMode && dailyN > 0) {
    const ident = document.createElement('p');
    ident.className = 'flow-fht-banner-ident';
    ident.textContent = 'thruWord #' + dailyN;
    banner.appendChild(ident);
  }

  // Praise as the big heading
  const bannerHeading = document.createElement('h2');
  bannerHeading.className = 'flow-fht-banner-heading';
  bannerHeading.textContent = solved
    ? praiseForStars(gameState.stars_remaining, gameState.max_stars)
    : 'Nice try!';
  banner.appendChild(bannerHeading);

  // "Solved · 7 guesses · 1/3 hints" — guesses first. Jeff, 2026-09-26: "the
  // primary win here is how many guess and then how many hints. but if not
  // hints and 100 guesses..." The same line goes on the share and the card.
  const praise = document.createElement('p');
  praise.className = 'flow-fht-banner-praise';
  const revealsUsed = Number(gameState.reveals_used) || 0;
  if (solved) {
    let line = 'Solved \u00b7 ' + scoreLine(gameState);
    // A solve that leaned on word-reveals still counts — the day is simply
    // marked as assisted, never punished.
    if (revealsUsed > 0) line += ' \u00b7 assisted';
    praise.textContent = line;
  } else {
    // Not "every puzzle makes you sharper": that is a benefit claim, and the
    // platform's rule (2026-09-16, agreed 2026-09-21) is to describe what the
    // puzzle asks, never what it does to you.
    praise.textContent = testMode
      ? 'Tomorrow\u2019s is a new one.'
      : 'Tomorrow\u2019s is a new one \u2014 and your streak still counts.';
  }
  banner.appendChild(praise);

  // Streak line — the reason to come back tomorrow. Uses the number the
  // server just returned; falls back to a quiet /streak read (locked-day
  // revisits carry it in the stored play response).
  if (!testMode) {
    const streakLine = document.createElement('p');
    streakLine.className = 'flow-fht-banner-streak';
    banner.appendChild(streakLine);
    const known = Number(playResponse && playResponse.streak_after);
    if (known > 0) {
      streakLine.textContent = streakText(known);
    } else if (api) {
      api.get('games/fht/streak')
        .then((r) => {
          const n = Number(r && r.data && r.data.current_streak) || 0;
          if (n > 0) streakLine.textContent = streakText(n);
        })
        .catch(() => { /* quiet */ });
    }
  }

  // Countdown to the next puzzle (the player's local midnight). Nothing
  // left to play today is the point — the wait builds tomorrow's habit.
  if (!testMode) {
    banner.appendChild(buildCountdown());
  } else {
    const bannerSub = document.createElement('p');
    bannerSub.className = 'flow-fht-banner-sub';
    bannerSub.textContent = 'Ready for your next puzzle?';
    banner.appendChild(bannerSub);
  }

  // Tier buttons INSIDE the banner
  const puzzleIndex = opts.puzzleIndex || [];
  const currentTier = opts.currentTier || gameState.tier || 'easy';
  if (testMode && puzzleIndex.length > 0) {
    const bannerTierRow = document.createElement('div');
    bannerTierRow.className = 'flow-fht-banner-tier-row';

    /* one tier since 2.14.13: an unplayed puzzle of the daily kind. 2.14.31: picked at
       random, as the walker's Next is; with none left, the first in order as before. */
    const daily = puzzleIndex.filter((p) => String(p.tier).toLowerCase() === 'medium');
    const nextId = betaRandomUnplayedId(daily, gameState.puzzle_id) || betaNextUnplayedId(puzzleIndex, 'medium');
    if (nextId && nextId !== Number(gameState.puzzle_id)) {
      const arrow = document.createElement('button');
      arrow.type = 'button';
      arrow.className = 'flow-fht-tier-arrow';
      arrow.textContent = '\u2192';
      arrow.setAttribute('aria-label', 'Next puzzle');
      arrow.addEventListener('click', () => {
        const u = new URL(window.location.href);
        u.searchParams.set('fht_test', String(nextId));
        window.location.href = u.toString();
      });
      bannerTierRow.appendChild(arrow);
    }
    banner.appendChild(bannerTierRow);
  }

  container.appendChild(banner);

  // The completed chain (vertical, with the bridge compounds spelled out
  // in subtle text between adjacent words).
  const chainBlock = makeChainSummary(gameState);
  container.appendChild(chainBlock);
  } catch (e) {
    // If anything above failed, we still fall through to render the share
    // and feedback buttons below so the player is never left without them.
  }

  // ---- Share (sits directly under the chain — the prominent action) ----
  const shareSection = document.createElement('div');
  shareSection.className = 'flow-fht-share';

  const shareHeading = document.createElement('p');
  shareHeading.className = 'flow-fht-share-heading';
  shareHeading.textContent = 'Share';
  shareSection.appendChild(shareHeading);

  const shareRow = document.createElement('div');
  shareRow.className = 'flow-fht-share-row';

  const hasNativeShare = !!navigator.share;

  // Share: opens the phone's share sheet (Messages, WhatsApp, etc.), which
  // turns the link into the result card. Only shown when the phone actually
  // has a share sheet — on desktop there isn't one, so Copy is the action.
  if (hasNativeShare) {
    const shareBtn = makeShareButton('Share', true, () => shareResult(gameState, api));
    shareRow.appendChild(shareBtn);
  }

  // Copy: the reliable cross-platform path. A copied link, pasted into any
  // messaging app, forms the result card on both Android and iPhone — so
  // it's the safety net when a phone's share sheet misbehaves (notably
  // iPhone → Messages). Primary action on desktop, where there's no Share.
  const copyBtn = makeShareButton('Copy', !hasNativeShare, () => copyLink(gameState, live, copyBtn));
  shareRow.appendChild(copyBtn);

  // Email is the one channel that can't unfurl a link, so it gets a real
  // subject + a friendly invite line + the link.
  shareRow.appendChild(makeShareButton('Email', false, () => openEmailShare(gameState)));

  shareSection.appendChild(shareRow);
  container.appendChild(shareSection);

  // 2.14.35, group E: the cognitive uses, under the share, as on every other game's end sheet. The words, the
  // every-fifth offer, the share and the article are the platform's (assets/shared/flow-finish-v1.js); this screen
  // only names the game and the puzzle.
  try {
    if (window.flowFinish && typeof window.flowFinish.usesBlock === 'function') {
      const uses = window.flowFinish.usesBlock({
        game: 'thruword',
        id: Number(gameState.daily_number) || gameState.puzzle_id || '',
      });
      uses.classList.add('flow-fht-uses-block');
      container.appendChild(uses);
    }
  } catch (e) { /* non-fatal: the end screen stands without it */ }

  // Feedback: the platform's one button, outside this mount. Nothing here.

  // Stats are no longer shown inline or auto-popped — they live behind the
  // header stats icon, reachable any time. In beta/test mode we record this
  // play locally first (real mode is recorded server-side).
  if (testMode) {
    try { recordBetaPlay(gameState); } catch (e) { /* non-fatal */ }
  }

  // Pull focus to the banner so screen readers announce the result.
  if (banner) setTimeout(() => focusElement(banner), 0);
  try {
    live.announce((solved ? 'Solved' : 'Failed') + '. ' + formatBannerMeta(gameState));
  } catch (e) { /* non-fatal */ }

  // Pre-fetch the result card the moment the screen opens, so tapping Share
  // attaches it instantly (the bytes are cached for every share after).
  if (!testMode) {
    try { preloadShareCard(gameState, api); } catch (e) { /* non-fatal */ }
  }
}

// ----------------------------------------------------------------------
// Chain summary block — locked words and the compounds between them.
// ----------------------------------------------------------------------

function makeChainSummary(state) {
  const block = document.createElement('div');
  block.className = 'flow-fht-chain-summary';

  const allWords = [state.start_word, ...state.slots_filled, state.end_word];
  // Render each word as a tile, with the compound formed between adjacent
  // words shown subtly in the gap.
  for (let i = 0; i < allWords.length; i++) {
    const tile = document.createElement('div');
    const isBridge = i > 0 && i < allWords.length - 1;
    const wasRevealed = isBridge && state.slot_revealed[i - 1];
    tile.className = 'flow-fht-tile flow-fht-tile-summary' +
      (wasRevealed ? ' flow-fht-tile-revealed' : '');
    tile.textContent = String(allWords[i] || '').toUpperCase();
    block.appendChild(tile);

    if (i < allWords.length - 1) {
      const compound = document.createElement('p');
      compound.className = 'flow-fht-compound-label';
      // 2.14.26: the same form the lock flash uses, so a two-word compound keeps its
      // space here too (PINE NUT, not PINENUT). This line glued every compound until now.
      compound.textContent = compoundDisplay(allWords[i], allWords[i + 1]).toUpperCase();
      block.appendChild(compound);
    }
  }
  return block;
}

// ----------------------------------------------------------------------
// Share.
// ----------------------------------------------------------------------

function makeShareButton(label, primary, onClick) {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'flow-fht-share-btn' + (primary ? ' flow-fht-share-btn-primary' : '');
  b.textContent = label;
  b.addEventListener('click', onClick);
  return b;
}

// A friendly one-line invitation that includes the result and the link.
// Used for the Email body (email can't unfurl a link into the card, so it
// needs words). Share itself sends the bare link so it unfurls.
function buildInviteText(state) {
  const link = buildShareText(state);
  const slots = (state.slots_filled || []).length || 1;
  const connect = slots > 1 ? 'words that connect' : 'word that connects';
  const n = Number(state.daily_number) || 0;
  const name = n > 0 ? 'thruWord #' + n : 'today\u2019s thruWord';
  if (state.status === 'solved') {
    return 'I solved ' + name + ' \u2014 ' + scoreLine(state) +
      ' \u2014 can you find the ' + connect + '? ' + link;
  }
  return 'I just played ' + name + ' \u2014 can you find the ' + connect + '? ' + link;
}

function openEmailShare(state) {
  const subject = 'Can you solve today\u2019s thruWord?';
  const body = buildShareText(state);
  window.location.href = 'mailto:?subject=' + encodeURIComponent(subject) +
    '&body=' + encodeURIComponent(body);
}

// Native share sheet (mobile only — wired up only when navigator.share
// exists). Hand the link to the messaging app in the `text` field, NOT the
// `url` field. This is the method that worked through 1.2.6: Google Messages
// (RCS) builds its rich preview card ("thruWord … Solved with X of Y
// hints") from a URL it finds in the message text. Switching to the `url`
// field at ~1.2.7 silently killed the card on Android — confirmed by testing
// (a clean https link sent via `url` shows as a plain link with no card).
// The link is an absolute https:// URL (see buildShareText) so it is
// recognised immediately and can never arrive doubled.
// Image-first share. The receiver previously saw a bare link arrive first,
// with the result card only appearing a couple of seconds later once the
// messaging app finished unfurling the URL. To put the picture first, we
// hand the pre-rendered result card to the share sheet as an actual image
// file, with the play link attached as text underneath.
//
// Reliability: the card image is fetched once when the result screen opens
// (preloadShareCard) and the downloaded bytes are cached. Every share — first,
// second, or tenth — builds a fresh image file from those cached bytes, so a
// repeat share can't silently fall back to the old link-only behaviour. If the
// preload hasn't finished (or failed), we fetch on the spot, and if that also
// fails we fall back to the proven text-link share so sharing can never break.
//
// When the image attaches we send the *clean* puzzle link (just the puzzle id)
// as the text — the picture already shows the result, so the link stays tidy.
// Only the link-only fallback carries the hint counts, so its auto-preview can
// still show the result.
let cachedCardBytes = null; // Promise<Blob|null> for the current result screen.

function preloadShareCard(state, api) {
  cachedCardBytes = fetchShareCardBytes(state, api).catch(() => null);
}

async function shareResult(state, api) {
  const text = buildShareText(state);

  // Attach the result-card picture whenever the phone's share sheet accepts
  // files — the receiver sees the image instantly, no unfurl gamble. The
  // text still carries the full visual + link, so even if a messaging app
  // mishandles the attachment, the share stands on its own.
  try {
    let blob = cachedCardBytes ? await cachedCardBytes : null;
    if (!blob) blob = await fetchShareCardBytes(state, api);
    if (blob && navigator.canShare && typeof File === 'function') {
      const file = new File([blob], 'thruword.png', { type: 'image/png' });
      if (navigator.canShare({ files: [file], text: text })) {
        await navigator.share({ files: [file], text: text });
        return;
      }
    }
  } catch (e) {
    if (e && e.name === 'AbortError') return; // user closed the sheet — done
    // Anything else falls through to the text-only share below.
  }

  try {
    await navigator.share({ text: text });
  } catch (e) { /* user cancelled — nothing to do */ }
}

// Ask the server for the result card image URL, then download the image bytes
// so they can be wrapped into a share file. Returns a Blob, or null on any
// failure (which tells shareResult to fall back to the link-only share).
async function fetchShareCardBytes(state, api) {
  if (!state || !state.puzzle_id || !api) return null;
  let path = 'games/fht/card?puzzle_id=' + encodeURIComponent(state.puzzle_id);
  if (state.status === 'solved') {
    const used = Math.max(0, (state.max_stars || 0) - (state.stars_remaining || 0));
    path += '&fh=' + used + '&fm=' + (state.max_stars || 0);
    const g = guessCount(state);
    if (g > 0) path += '&fg=' + g;
  }
  let cardUrl = '';
  try {
    const res = await api.get(path);
    cardUrl = (res && res.data && res.data.card_url) || '';
  } catch (e) { return null; }
  if (!cardUrl) return null;
  let resp;
  try { resp = await fetch(cardUrl, { credentials: 'same-origin', cache: 'force-cache' }); }
  catch (e) { return null; }
  if (!resp || !resp.ok) return null;
  const blob = await resp.blob();
  if (!blob || !blob.size) return null;
  return blob;
}

// Copy the link to the clipboard and confirm on the button. A pasted link
// forms the result card on both Android and iPhone, so this is the reliable
// fallback when a share sheet misbehaves.
function copyLink(state, live, btn) {
  copyText(buildShareText(state), live);
  flashButton(btn, 'Link copied');
}

function flashButton(btn, msg) {
  if (!btn) return;
  const original = btn.textContent;
  btn.textContent = msg;
  btn.disabled = true;
  setTimeout(() => {
    btn.textContent = original;
    btn.disabled = false;
  }, 1500);
}

function copyText(text, live) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(
      () => live.announce('Link copied.'),
      () => fallbackCopy(text, live),
    );
    return;
  }
  fallbackCopy(text, live);
}

function fallbackCopy(text, live) {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'absolute';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
    live.announce('Link copied.');
  } catch (e) {
    live.announce('Couldn\u2019t copy. Link shown below.');
    window.prompt('Share link', text);
  } finally {
    document.body.removeChild(ta);
  }
}

// The base URL for share links: the page's own address (handed in by the
// shortcode as shareBase), so links always match whatever slug the page uses —
// rename the page and shares keep working, no code change. Falls back to this
// page's location if the config isn't present.
// ----------------------------------------------------------------------
// Daily-model banner helpers.
// ----------------------------------------------------------------------

function streakText(n) {
  return n === 1
    ? 'Streak: 1 day \u2014 come back tomorrow to keep it going!'
    : 'Streak: ' + n + ' days \u2014 keep it alive!';
}

// Live countdown to the player's local midnight, when the next puzzle
// arrives. Ticks once a second; at zero it flips to a refresh invitation.
function buildCountdown() {
  const p = document.createElement('p');
  p.className = 'flow-fht-banner-sub flow-fht-countdown';

  function msToLocalMidnight() {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
    return next.getTime() - now.getTime();
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  let timer = null;
  function tick() {
    const ms = msToLocalMidnight();
    if (ms <= 1000) {
      p.textContent = 'A new thruWord is ready \u2014 refresh to play!';
      if (timer) clearInterval(timer);
      return;
    }
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    p.textContent = 'Next thruWord in ' + h + ':' + pad(m) + ':' + pad(s);
  }
  tick();
  timer = setInterval(tick, 1000);
  return p;
}

// THE BOTTOM SHARE (2.14.17; Jeff, 2026-09-29: "next to the feedback button should be
// a share button. put the share to the left of the feedback."). On screen from the
// first second, like every game's: once the puzzle is solved it sends the same result
// the result screen does; before that, an invitation — the two words everyone sees on
// the board, and the link. Never a hidden word.
// 2.14.29: the platform's bottom Share (printed by the shortcode, outside the mount) is on
// the game screen and off the end screen, which has its own.
export function showBottomShare(on) {
  try {
    const b = document.getElementById('flow-fht-share');
    if (b) b.style.display = on ? '' : 'none';
  } catch (e) { /* the page has no bottom row: nothing to do */ }
}

export function bottomShareText(state) {
  state = state || {};
  if (state.status === 'solved') return buildShareText(state);
  const n = Number(state.daily_number) || 0;
  const start = String(state.start_word || '').toUpperCase();
  const end = String(state.end_word || '').toUpperCase();
  return (n > 0 ? 'thruWord #' + n : 'thruWord') + '\n' + start + ' \u2192 ' + end +
    '\nCan you connect them?\n' + shareBaseUrl();
}

export async function bottomShare(state, btn) {
  const text = bottomShareText(state);
  if (navigator.share) {
    try { await navigator.share({ text: text }); } catch (e) { /* closed the sheet */ }
    return;
  }
  copyText(text, { announce() {} });
  flashButton(btn, 'Copied');
}

function shareBaseUrl() {
  const cfg = (typeof window !== 'undefined' && window.flowFhtConfig) || {};
  let base = cfg.shareBase || '';
  if (!base) base = window.location.origin + window.location.pathname;
  if (base.charAt(base.length - 1) !== '/') base += '/';
  return base;
}

function buildShareText(state) {
  // The message text IS the share now — it carries the result visually and
  // never depends on a preview card. One colored square per hidden word:
  // green = found clean, yellow = a letter hint helped, white = revealed.
  // The card (when a messaging app builds one) is a bonus underneath.
  const start = String(state.start_word || '').toUpperCase();
  const end = String(state.end_word || '').toUpperCase();
  const n = Number(state.daily_number) || 0;
  let line1 = (n > 0 ? 'thruWord #' + n : 'thruWord');
  if (state.status === 'solved') {
    line1 += ' \u00b7 ' + scoreLine(state);   // guesses, then hints
    const t = fmtShareTime(state);
    if (t) line1 += ' \u00b7 ' + t;
  }
  const line2 = start + ' ' + resultSquares(state) + ' ' + end;
  return line1 + '\n' + line2 + '\nCan you connect them?\n' + buildPlayUrl(state);
}

// One square per hidden word. Green: the player found it clean. Yellow: a
// letter hint helped. White: it was revealed. Honest and spoiler-free —
// the squares show HOW the day went without giving away a single word.
const SQ_CLEAN  = '\uD83D\uDFE9'; // green square
const SQ_HINT   = '\uD83D\uDFE8'; // yellow square
const SQ_REVEAL = '\u2B1C';       // white square

function resultSquares(state) {
  const par = (state.slots_filled || []).length;
  let out = '';
  for (let i = 0; i < par; i++) {
    if (state.slot_revealed && state.slot_revealed[i]) out += SQ_REVEAL;
    else if (state.letters_revealed && Number(state.letters_revealed[i]) > 0) out += SQ_HINT;
    else out += SQ_CLEAN;
  }
  return out;
}

function fmtShareTime(state) {
  const s = activeSeconds(state);
  if (!s) return '';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m + ':' + (r < 10 ? '0' : '') + r;
}

// The daily play link: the clean bare URL, the same for everyone all day
// (one puzzle a day since 2.14.13 — the ?lvl=2 variant is gone).
function buildPlayUrl(state) {
  return shareBaseUrl();
}

// ----------------------------------------------------------------------
// Helpers.
// ----------------------------------------------------------------------

function praiseForStars(remaining, max) {
  const used = max - remaining;
  if (used === 0) return 'Brilliant!';
  if (used === 1) return 'Excellent!';
  if (used === 2) return 'Well done!';
  if (used === 3) return 'Nice solve!';
  return 'You got there!';
}

function formatBannerMeta(state) {
  return scoreLine(state);
}

// The number of checks the player made across the whole chain. game.js
// counts it in state.guess_count; older saved states may not carry it.
export function guessCount(state) {
  return Math.max(0, Number(state && state.guess_count) || 0);
}

// "7 guesses · 1/3 hints" — the one form of the score, used by the banner,
// the share text, the email invite and the live announcement. Guesses lead.
export function scoreLine(state) {
  const g = guessCount(state);
  const used = Math.max(0, (state.max_stars || 0) - (state.stars_remaining || 0));
  const hints = used + '/' + (state.max_stars || 0) + ' hints';
  if (g <= 0) return hints;
  return g + (g === 1 ? ' guess' : ' guesses') + ' \u00b7 ' + hints;
}
